import { useCallback } from 'react';
import { useTenantID } from '@/hooks/useTenantID';
import { DO_NOT_USE_ID, normalizeImportHeader } from '@/hooks/useFieldMappings';
import { useAuth } from '@/stores/auth';
import { useImportMappingMemory } from '@/stores/importMappingMemory';
import type { StudentFormType } from '@/hooks/useStudentFormSchema';
import type { MappedColumns } from '@/components/Import/ImportColumnMapper';

export const useRememberedImportMappings = (type: StudentFormType) => {
  const tenantId = useTenantID();
  const organizationId = useAuth((state) => state.userProfile?.organization_id);
  const scope = `${tenantId}:${organizationId ?? ''}:${type}`;

  const remembered = useImportMappingMemory((state) => state.mappings[scope]);
  const rememberInStore = useImportMappingMemory((state) => state.remember);

  const findRememberedField = useCallback(
    (header: string): string | undefined =>
      remembered?.[normalizeImportHeader(header)],
    [remembered],
  );

  const rememberMappings = useCallback(
    (mappedColumns: MappedColumns) => {
      const columns: Record<string, string> = {};
      for (const [header, apiField] of Object.entries(mappedColumns)) {
        const normalizedHeader = normalizeImportHeader(header);
        if (!normalizedHeader) continue;
        columns[normalizedHeader] = apiField.startsWith(DO_NOT_USE_ID)
          ? DO_NOT_USE_ID
          : apiField;
      }
      rememberInStore(scope, columns);
    },
    [scope, rememberInStore],
  );

  return { findRememberedField, rememberMappings };
};
